import React from "react";
import { EditButton } from "./EditButton";
import countryCodes from "../data/countryCodes.json";

interface UserDetailsProps {
  user: any;
  isEditingStreetAddress: boolean;
  isEditingCity: boolean;
  isEditingState: boolean;
  isEditingCountry: boolean;
  isEditingPostalCode: boolean;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  onStreetAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCountryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPostalCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStreetAddressEdit: () => void;
  onCityEdit: () => void;
  onStateEdit: () => void;
  onCountryEdit: () => void;
  onPostalCodeEdit: () => void;
  onSave: (field: string, value: string) => Promise<void>;
}

export default function UserDetails({
  user,
  isEditingStreetAddress,
  isEditingCity,
  isEditingState,
  isEditingCountry,
  isEditingPostalCode,
  streetAddress,
  city,
  state,
  country,
  postalCode,
  onStreetAddressChange,
  onCityChange,
  onStateChange,
  onCountryChange,
  onPostalCodeChange,
  onStreetAddressEdit,
  onCityEdit,
  onStateEdit,
  onCountryEdit,
  onPostalCodeEdit,
  onSave,
}: UserDetailsProps) {
  const countryNames = Object.keys(countryCodes).sort();

  const handleSave = async (field: string, value: string, editToggle: () => void) => {
    await onSave(field, value);
    editToggle();
  };

  return (
    <div style={{ maxWidth: "500px" }} className="mt-8 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-4">User Details</h2>
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-500">Email</label>
          <input
            type="email"
            value={user?.email}
            disabled
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500">Street Address</label>
          <div className="relative">
            <input
              type="text"
              value={streetAddress}
              disabled={!isEditingStreetAddress}
              onChange={onStreetAddressChange}
              placeholder="Enter street address"
              className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingStreetAddress
                ? "bg-gray-50 dark:bg-gray-700"
                : "bg-white dark:bg-gray-800"
                } text-gray-900 dark:text-gray-100`}
            />
            <EditButton
              isEditing={isEditingStreetAddress}
              onClick={isEditingStreetAddress
                ? () => handleSave('streetAddress', streetAddress, onStreetAddressEdit)
                : onStreetAddressEdit
              }
            />
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-500">City</label>
          <div className="relative">
            <input
              type="text"
              value={city}
              disabled={!isEditingCity}
              onChange={onCityChange}
              placeholder="Enter city"
              className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingCity
                ? "bg-gray-50 dark:bg-gray-700"
                : "bg-white dark:bg-gray-800"
                } text-gray-900 dark:text-gray-100`}
            />
            <EditButton
              isEditing={isEditingCity}
              onClick={isEditingCity
                ? () => handleSave('city', city, onCityEdit)
                : onCityEdit
              }
            />
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-500">State / Province</label>
          <div className="relative">
            <input
              type="text"
              value={state}
              disabled={!isEditingState}
              onChange={onStateChange}
              placeholder="Enter state or province"
              maxLength={2}
              className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingState
                ? "bg-gray-50 dark:bg-gray-700"
                : "bg-white dark:bg-gray-800"
                } text-gray-900 dark:text-gray-100`}
            />
            <EditButton
              isEditing={isEditingState}
              onClick={isEditingState
                ? () => handleSave('state', state, onStateEdit)
                : onStateEdit
              }
            />
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-500">Country</label>
          <div className="relative">
            {isEditingCountry ? (
              <select
                value={country}
                onChange={onCountryChange}
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select your country</option>
                {countryNames.map((countryName) => (
                  <option key={countryName} value={countryName}>
                    {countryName}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={country}
                disabled
                placeholder="Select your country"
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            )}
            <EditButton
              isEditing={isEditingCountry}
              onClick={isEditingCountry
                ? () => handleSave('country', country, onCountryEdit)
                : onCountryEdit
              }
            />
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-500">Postal Code</label>
          <div className="relative">
            <input
              type="text"
              value={postalCode}
              disabled={!isEditingPostalCode}
              onChange={onPostalCodeChange}
              placeholder="Enter postal code"
              maxLength={7}
              className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingPostalCode
                ? "bg-gray-50 dark:bg-gray-700"
                : "bg-white dark:bg-gray-800"
                } text-gray-900 dark:text-gray-100`}
            />
            <EditButton
              isEditing={isEditingPostalCode}
              onClick={isEditingPostalCode
                ? () => handleSave('postalCode', postalCode, onPostalCodeEdit)
                : onPostalCodeEdit
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
